/**
 * Colour resolution for the canvas map renderer (WI #5531).
 *
 * The SVG renderer styles elements with CSS custom properties (`--color-*`) that
 * resolve against the active Beeboard theme. Canvas needs concrete colour strings
 * for `fillStyle` / `strokeStyle`, so we resolve those variables once from
 * `:root` and cache them. Hard-coded hexes are kept as fallbacks (and to match the
 * SVG exactly) in case resolution returns empty (e.g. during SSR/export).
 *
 * Colour provenance (retired SVG renderer, deleted in WI #5609):
 *  - hexagon fill/label  → svg/parts/MapActivityMarker.vue
 *  - label rect/text     → svg/parts/MapActivityLabel.vue
 *  - lines / backgrounds → svg/parts/MapTimeItem.vue, MapPhaseBackground.vue,
 *                          MapPastTime.vue, MapMilestoneLine.vue, MapAssets.vue
 */
import {
  getItemMode,
  type ItemMode,
  type ItemState,
  type ItemStatus,
} from '@/common';

const varCache = new Map<string, string>();

/**
 * Resolve a CSS custom property from :root, cached. Falls back to `fallback`
 * when the variable is undefined or empty (non-browser contexts).
 */
export function cssVar(name: string, fallback: string): string {
  const cached = varCache.get(name);
  if (cached !== undefined) {
    return cached;
  }

  let value = fallback;
  if (typeof document !== 'undefined' && document.documentElement) {
    const resolved = getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
    if (resolved) {
      value = resolved;
    }
  }

  varCache.set(name, value);

  return value;
}

/** Drop the resolution cache so a theme switch re-reads `--color-*`. */
export function clearPaletteCache(): void {
  varCache.clear();
}

/** Hexagon fill colour used when activity colours are hidden (status palette). */
export function hexagonStatusFill(mode: ItemMode): string {
  switch (mode) {
    case 'unplanned':
      return cssVar('--color-bluegray-0', '#FFFFFF');
    case 'planned':
      return cssVar('--color-bluegray-200', '#C8CCE5');
    case 'done':
      return cssVar('--color-bluegray-900', '#101323');
    case 'underway-green':
      return cssVar('--color-success-300', '#6CE9A6');
    case 'underway-yellow':
      return cssVar('--color-warning-300', '#FEC84B');
    case 'underway-red':
      return cssVar('--color-error-300', '#FDA29B');
    case 'underway':
    default:
      return cssVar('--color-bluegray-400', '#717BBC');
  }
}

/** Sequence-number colour inside the hexagon (white on the dark `done` fill). */
export function hexagonLabelColor(mode: ItemMode, fallback?: string): string {
  if (mode === 'done') {
    return '#FFFFFF';
  }

  return fallback ?? '#000000';
}

/**
 * Fill and sequence-number colour of an activity's hexagon — HexagonsPainter,
 * and MapGroupList, whose rows stand in for the hexagons of a collapsed group.
 * `finalColors` is `activityFinalColors(id)`, which carries no colour in status
 * view (`statusView`), so a normal hexagon then takes the status fill. A
 * mirrored hexagon is white with a black number by default, but in status view
 * it also takes the status fill (except unplanned, which stays white) —
 * matching the SVG `.hexagon-mirrored` overrides.
 */
export function activityHexagonColors(
  activity: { state: ItemState; status?: ItemStatus; mirrored?: boolean },
  statusView: boolean,
  finalColors: { color?: string; label?: string },
): { fill: string; text: string } {
  const mode = modeOf(activity.state, activity.status);
  if (activity.mirrored) {
    return {
      fill:
        statusView && mode !== 'unplanned'
          ? hexagonStatusFill(mode)
          : '#FFFFFF',
      text: statusView ? hexagonLabelColor(mode, undefined) : '#000000',
    };
  }

  return {
    fill: finalColors.color ?? hexagonStatusFill(mode),
    // Status view forces white on the dark done fill; normal view uses the
    // function's label colour (SVG MapActivityMarker's `colors.label`).
    text: statusView
      ? hexagonLabelColor(mode, finalColors.label)
      : (finalColors.label ?? '#000000'),
  };
}

export interface LabelColors {
  stroke: string;
  fill: string;
  text: string;
}

/**
 * Label rounded-rect + text colours, mirroring MapActivityLabel.vue.
 *
 * Note on text colour: the SVG label's `<text>` has classes
 * `text-label activity-label-caption` and a hardcoded `fill="#333"`. The
 * per-state `.text { fill: … }` overrides in MapActivityLabel target the class
 * `.text`, which the element does NOT have — so they are dead CSS and never
 * apply. Only the `.activity-label-caption { fill: … }` overrides (under
 * `[data-state='underway']`) actually take effect. Hence the title is `#333`
 * for unplanned / planned / done, and only the underway states recolour it.
 * (The `.background` rect overrides DO apply — the rect has class `background` —
 * so the fill/stroke below are state-driven as in the SVG.)
 */
export function labelColors(mode: ItemMode): LabelColors {
  switch (mode) {
    case 'unplanned':
      return {
        stroke: cssVar('--color-gray-100', '#F2F4F7'),
        fill: '#FFFFFF',
        text: '#333333',
      };
    case 'planned':
      return {
        stroke: cssVar('--color-bluegray-200', '#C8CCE5'),
        fill: cssVar('--color-bluegray-50', '#F8F9FC'),
        text: '#333333',
      };
    case 'underway-green':
      return {
        stroke: cssVar('--color-success-200', '#A6F4C5'),
        fill: cssVar('--color-success-50', '#ECFDF3'),
        text: cssVar('--color-success-900', '#054F04'),
      };
    case 'underway-yellow':
      return {
        stroke: cssVar('--color-warning-200', '#FEDF89'),
        fill: cssVar('--color-warning-50', '#FFF7E0'),
        text: cssVar('--color-warning-800', '#93370D'),
      };
    case 'underway-red':
      return {
        stroke: cssVar('--color-error-200', '#FFCDCA'),
        fill: cssVar('--color-error-50', '#FEF3F2'),
        text: cssVar('--color-error-800', '#912018'),
      };
    case 'done':
      return {
        stroke: cssVar('--color-gray-300', '#D0D5DD'),
        fill: '#FFFFFF',
        text: '#333333',
      };
    case 'underway':
    default:
      return {
        stroke: cssVar('--color-bluegray-400', '#717BBC'),
        fill: cssVar('--color-bluegray-50', '#F8F9FC'),
        text: '#000000',
      };
  }
}

/**
 * Link arrow stroke colour per state/status, mirroring the `.mode-*` table of the
 * retired SVG renderer (`MapArrow.vue:121-147`): each mode has its own shade —
 * they are NOT collapsed. `done` and the base/notRequired fallback share gray-700.
 */
export function linkColor(mode: ItemMode): string {
  switch (mode) {
    case 'unplanned':
      return cssVar('--color-primary-700', '#38587C');
    case 'planned':
      return cssVar('--color-primary-400', '#6F8DB6');
    case 'underway':
      return cssVar('--color-bluegray-300', '#9EA5D1');
    case 'underway-green':
      return cssVar('--color-success-500', '#12B76A');
    case 'underway-yellow':
      return cssVar('--color-warning-400', '#FDB022');
    case 'underway-red':
      return cssVar('--color-error-600', '#D92D20');
    case 'done':
    default:
      return cssVar('--color-gray-700', '#344054');
  }
}

/** WI #5094: colour of the overdue clock on phases and milestones. */
export function overdueColor(): string {
  return cssVar('--color-error-600', '#D92D20');
}

/** Convenience: state+status → ItemMode (re-export of the shared helper). */
export function modeOf(state: ItemState, status?: ItemStatus): ItemMode {
  return getItemMode(state, status);
}
